import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { SITE_URL } from '../components/SEO';
import { Blog7 } from '../components/ui/blog7';
import blogPosts from '../data/blogPosts';
import {
  Newspaper,
  RefreshCw,
  BellRing,
  ArrowRight,
} from 'lucide-react';

const Blog = () => {
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Header />
      <SEO
        title="Blog — Influencer Marketing Insights & Trends"
        description="Expert insights, industry trends, and best practices for influencer marketing and authentic product reviews. Campaign spotlights, tips for brands and creators."
        url="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Engaged Manila Blog",
          "url": `${SITE_URL}/blog`,
          "publisher": {
            "@type": "Organization",
            "name": "Engaged Manila",
            "url": SITE_URL,
          },
          "blogPost": blogPosts.map((p) => ({
            "@type": "BlogPosting",
            "headline": p.title,
            "url": `${SITE_URL}/blog/${p.id}`,
            "datePublished": new Date(p.published.replace(/(\d{2}) (\w{3}) (\d{4})/, '$2 $1, $3')).toISOString(),
            "author": { "@type": "Organization", "name": "Engaged Manila" },
            "image": `${SITE_URL}${p.image}`,
          })),
        }}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/90 to-blue-800 pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96">
            <motion.div
              className="w-full h-full rounded-full bg-white/5"
              animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <div className="absolute -bottom-32 -left-32 w-96 h-96">
            <motion.div
              className="w-full h-full rounded-full bg-blue-400/5"
              animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '20px 20px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full bg-blue-400/10 backdrop-blur-sm border border-blue-300/20">
              <span className="text-sm font-medium text-blue-100">Engaged Manila</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Insights & Perspectives
            </h1>
            <p className="mt-6 text-xl leading-8 text-blue-100">
              Expert insights, industry trends, and best practices for influencer marketing and authentic product reviews
            </p>
          </motion.div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <Blog7
        tagline="Latest Articles"
        heading="From Our Blog"
        description="Expert insights, industry trends, and best practices for influencer marketing and authentic product reviews."
        buttonText="Explore all posts"
        buttonUrl="/blog"
        posts={blogPosts.map((p) => ({
          id: p.id,
          title: p.title,
          summary: p.summary,
          label: p.label,
          author: p.author,
          published: p.published,
          url: `/blog/${p.id}`,
          image: p.image,
        }))}
      />

      {/* What to Expect Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">What to Expect</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Topics We Cover
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Our blog features expert content designed to help you navigate the influencer marketing landscape
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Newspaper,
                title: 'Industry Insights',
                description: 'In-depth analysis of trends, technologies, and strategies shaping the future of influencer marketing and authentic product reviews.',
              },
              {
                icon: RefreshCw,
                title: 'Campaign Spotlights',
                description: 'Real campaign case studies from Engaged Manila showing how brands and influencers achieved results across Fashion, Food, Fitness, and more.',
              },
              {
                icon: BellRing,
                title: 'Tips & Best Practices',
                description: 'Actionable advice and step-by-step guides to help you create, manage, and optimize influencer marketing campaigns.',
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-500">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <motion.div
        className="relative isolate overflow-hidden bg-gray-900 rounded-3xl mx-6 mb-16 py-16 sm:py-24 lg:py-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
            <div className="max-w-xl lg:max-w-lg">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Stay updated</h2>
              <p className="mt-4 text-lg leading-8 text-gray-300">
                Subscribe to our newsletter to receive notifications when we publish new content.
              </p>
              <div className="mt-6 flex max-w-md gap-x-4">
                <label htmlFor="email-address" className="sr-only">Email address</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="min-w-0 flex-auto rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 placeholder:text-gray-400"
                  placeholder="Enter your email"
                />
                <button
                  type="submit"
                  className="flex-none rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all duration-200"
                >
                  Subscribe
                </button>
              </div>
            </div>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2">
              <div className="flex flex-col items-start">
                <div className="rounded-md bg-white/5 p-2 ring-1 ring-white/10">
                  <Newspaper className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <dt className="mt-4 font-semibold text-white">Weekly articles</dt>
                <dd className="mt-2 leading-7 text-gray-400">Receive our latest articles, insights, and updates directly to your inbox every week.</dd>
              </div>
              <div className="flex flex-col items-start">
                <div className="rounded-md bg-white/5 p-2 ring-1 ring-white/10">
                  <RefreshCw className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <dt className="mt-4 font-semibold text-white">No spam</dt>
                <dd className="mt-2 leading-7 text-gray-400">We respect your inbox. We'll only send relevant content that adds value to your business.</dd>
              </div>
            </dl>
          </div>
        </div>
        <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl xl:-top-6" aria-hidden="true">
          <div
            className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-primary to-blue-800 opacity-30"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default Blog;
